/**
 * Shared API helper utilities
 *
 * Centralizes common fetch patterns to reduce duplication across hooks
 */

/**
 * Build a query string from an object of parameters
 *
 * Handles undefined, null, and empty string values by excluding them
 * Converts all values to strings for URLSearchParams
 *
 * @example
 * buildQueryString({ search: 'test', limit: 10 }) // '?search=test&limit=10'
 * buildQueryString({ search: '' }) // ''
 * buildQueryString(undefined) // ''
 */
export function buildQueryString<T extends object>(params?: T): string {
  if (!params) return ''

  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Type-safe fetch wrapper with automatic credentials and error handling
 *
 * @throws Error if response is not ok (status 4xx or 5xx)
 *
 * @example
 * const data = await fetchJson<ContactsResponse>('/api/contacts')
 */
export async function fetchJson<T>(
  url: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
  })

  if (!response.ok) {
    // Parse error response for user-friendly message, avoid leaking status codes
    const body = await response.json().catch(() => ({}))
    const message = (body as { error?: string }).error || 'Request failed'
    throw new Error(message)
  }

  return response.json()
}

/**
 * Convenience wrapper for mutation requests (POST, PUT, DELETE)
 *
 * Automatically sets Content-Type header and stringifies body
 *
 * @example
 * const contact = await fetchMutation<ContactResponse>('/api/contacts', 'POST', { name: 'John' })
 * await fetchMutation<DeleteResponse>('/api/contacts/123', 'DELETE')
 */
export async function fetchMutation<T>(
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown
): Promise<T> {
  return fetchJson<T>(url, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
}
